import React, { useMemo } from "react";
import {
  Box, CircularProgress, Paper, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Tooltip, Typography,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import { useGetPrivilegesQuery } from "../../../Api/attoDeskApi";
import { IPrivilege } from "../../../Api/Interface/api.interface";

const DOT_COLORS = {
  view:   "#0ea5e9",
  create: "#16a34a",
  edit:   "#f59e0b",
  delete: "#ef4444",
};

const PrivilegesPage: React.FC = () => {
  const { data, isFetching } = useGetPrivilegesQuery();
  const modules: IPrivilege[] = useMemo(() => (data?.data as IPrivilege[]) ?? [], [data]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ borderRadius: 3, bgcolor: "#2f3891", p: 3, mb: 3, display: "flex", alignItems: "center", gap: 2 }}>
        <Box sx={{ width: 44, height: 44, borderRadius: 2, bgcolor: "rgba(255,255,255,0.15)", display: "flex", alignItems: "center", justifyContent: "center" }}>
          <SecurityOutlinedIcon sx={{ color: "#fff", fontSize: 24 }} />
        </Box>
        <Box>
          <Typography fontWeight={700} fontSize={18} color="#fff">Privileges</Typography>
          <Typography fontSize={12} color="rgba(255,255,255,0.7)">{modules.length} modules available in the system</Typography>
        </Box>
      </Box>

      {/* Table */}
      <Paper elevation={0} sx={{ border: "1px solid #e2e8f0", borderRadius: 3, overflow: "hidden" }}>
        {isFetching ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: "#2f3891" }} />
          </Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ bgcolor: "#f8fafc" }}>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b", width: 50 }}>#</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Module Key</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: "#64748b" }}>Display Name</TableCell>
                  <TableCell sx={{ fontWeight: 600, color: DOT_COLORS.view }} align="center">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      <VisibilityOutlinedIcon sx={{ fontSize: 14 }} /> View
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: DOT_COLORS.create }} align="center">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      <AddCircleOutlineIcon sx={{ fontSize: 14 }} /> Create
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: DOT_COLORS.edit }} align="center">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      <EditOutlinedIcon sx={{ fontSize: 14 }} /> Edit
                    </Box>
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600, color: DOT_COLORS.delete }} align="center">
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5 }}>
                      <DeleteOutlineIcon sx={{ fontSize: 14 }} /> Delete
                    </Box>
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modules.map((mod, idx) => (
                  <TableRow key={mod.key} hover>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <Typography fontWeight={700} fontSize={12} color="#2f3891" sx={{ fontFamily: "monospace" }}>
                        {mod.key}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography fontSize={13} fontWeight={500} color="#1e293b">{mod.display}</Typography>
                    </TableCell>
                    {(["view", "create", "edit", "delete"] as const).map((action) => (
                      <TableCell key={action} align="center">
                        <Tooltip title={`All roles can be granted ${action} on ${mod.display}`}>
                          <CheckCircleIcon sx={{ fontSize: 18, color: DOT_COLORS[action] }} />
                        </Tooltip>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  );
};

export default PrivilegesPage;
